import { KeyboardEvent, useMemo } from 'react';

import { Button, Card } from 'antd';
import { useTranslation } from 'react-i18next';

import { joinParts } from '../utils/format';
import {
  RouteExecution,
  dayHeaderLabel,
  executionMatchesSelection,
} from '../utils/mapData';
import { VisitSelection } from '../utils/selection';

const I18N_BASE = 'scenes.visit_history.routes_panel';

interface VisitRoutesPanelProps {
  executions: RouteExecution[];
  /** The Routes KPI of the range. */
  total: number;
  visibleKeys: Set<string>;
  selection: VisitSelection;
  /** Checkbox: shows or hides the route on the map, nothing else. */
  onToggle: (key: string) => void;
  onShowAll: () => void;
  /** Row click: the route becomes the shared selection. */
  onSelectRoute: (execution: RouteExecution) => void;
}

/**
 * Route executions grouped by day, each with the color it has on the map. The
 * checkbox controls what the map draws; clicking the row selects the route for
 * every block of the screen.
 */
function VisitRoutesPanel({
  executions,
  total,
  visibleKeys,
  selection,
  onToggle,
  onShowAll,
  onSelectRoute,
}: VisitRoutesPanelProps) {
  const { t, i18n } = useTranslation();

  const days = useMemo(() => {
    const byDay = new Map<string, RouteExecution[]>();
    executions.forEach((execution) => {
      const day = byDay.get(execution.date);
      if (day) day.push(execution);
      else byDay.set(execution.date, [execution]);
    });

    return [...byDay.entries()];
  }, [executions]);

  const someHidden = executions.some((e) => !visibleKeys.has(e.key));

  const onRowKey = (event: KeyboardEvent, execution: RouteExecution) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelectRoute(execution);
    }
  };

  return (
    <Card
      className="visit-history-routes"
      title={t(`${I18N_BASE}.title`, { count: total })}
      extra={
        someHidden && (
          <Button type="link" size="small" onClick={onShowAll}>
            {t(`${I18N_BASE}.show_all`)}
          </Button>
        )
      }
      styles={{ body: { padding: 0 } }}
    >
      <div className="visit-history-routes__scroll">
        {days.length === 0 && (
          <div className="visit-history-routes__empty">
            {t(`${I18N_BASE}.empty`)}
          </div>
        )}
        {days.map(([date, rows]) => (
          <section key={date}>
            <div className="visit-history-routes__day">
              <span>{dayHeaderLabel(date, i18n.language)}</span>
              <span>{t(`${I18N_BASE}.day_count`, { count: rows.length })}</span>
            </div>
            {rows.map((execution) => {
              const selected =
                selection?.kind === 'route' &&
                executionMatchesSelection(execution, selection);
              const dimmed = !executionMatchesSelection(execution, selection);

              return (
                <div
                  key={execution.key}
                  role="button"
                  tabIndex={0}
                  className={[
                    'visit-history-routes__row',
                    selected ? 'is-selected' : '',
                    dimmed ? 'is-dimmed' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => onSelectRoute(execution)}
                  onKeyDown={(event) => onRowKey(event, execution)}
                >
                  <input
                    type="checkbox"
                    className="visit-history-routes__check"
                    style={{ accentColor: execution.color }}
                    checked={visibleKeys.has(execution.key)}
                    aria-label={t(`${I18N_BASE}.toggle`, {
                      code: execution.routeCode,
                    })}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => onToggle(execution.key)}
                  />
                  <span
                    className="visit-history-routes__bar"
                    style={{ background: execution.color }}
                  />
                  <span className="visit-history-routes__mid">
                    <span className="visit-history-routes__code">
                      {execution.routeCode}
                    </span>
                    <span
                      className="visit-history-routes__meta"
                      title={joinParts([execution.zoneName, execution.vanName])}
                    >
                      {joinParts([execution.zoneName, execution.vanName])}
                    </span>
                  </span>
                  <span className="visit-history-routes__ratio">
                    {execution.executed}/{execution.planned}
                  </span>
                </div>
              );
            })}
          </section>
        ))}
      </div>
    </Card>
  );
}

export default VisitRoutesPanel;
